import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Lock, Clock, Eye, KeyRound, FileText, Files as FilesIcon, Link2, Trash2, ShieldCheck, Zap, Building2, ArrowRight, Fingerprint, Server, MapPin } from 'lucide-react';

const FEATURES = [
  { i: Lock, t: 'Cifratura AES-256', d: 'Ogni push è cifrato con chiave derivata. Il DB conserva solo dati cifrati.' },
  { i: Clock, t: 'Scadenza temporale', d: 'Da 1 a 90 giorni. Alla scadenza il record viene eliminato automaticamente.' },
  { i: Eye, t: 'Limite visualizzazioni', d: 'Da 1 a 100 accessi. Ogni visualizzazione decrementa il contatore.' },
  { i: KeyRound, t: 'Passphrase opzionale', d: 'Difesa a doppio canale: URL su email, passphrase su SMS.' },
  { i: FileText, t: 'Testo', d: 'Password, chiavi API, token, appunti riservati.' },
  { i: FilesIcon, t: 'File', d: 'Allega file fino a 5MB — configurazioni, certificati, PDF.' },
  { i: Link2, t: 'URL', d: 'Condividi link temporanei senza esporli in email o chat.' },
  { i: Trash2, t: 'Distruzione immediata', d: 'Il destinatario può eliminare il contenuto subito dopo l’uso.' },
  { i: ShieldCheck, t: 'GDPR compliant', d: 'Dati in EU, minimizzazione e nessun profilo utente.' },
  { i: Fingerprint, t: 'Nessun tracking', d: 'Niente cookie di profilazione, niente pixel di terze parti.' },
  { i: Server, t: 'Auto-eliminazione', d: 'Job periodico ripulisce i push scaduti dal database.' },
  { i: Building2, t: 'Personalizzato Tecnologie', d: 'Marchio, colori e testi ottimizzati per la tua clientela.' },
];

export default function Features() {
  return (
    <div>
      <section className="tv-gradient-soft py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0d63b0]">Funzionalità</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-slate-900">Tutto ciò che serve. Niente più.</h1>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Un servizio essenziale, veloce e sicuro per condividere segreti. Ecco cosa include la versione Tecnologie.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <Card key={f.t} className="tv-card-hover border border-slate-200 p-6">
                <div className="h-11 w-11 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <f.i className="h-5 w-5 text-[#0d63b0]" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900">{f.t}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50/70">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0d63b0]">Domande frequenti</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-slate-900">Tutte le domande. Una risposta.</h2>
          </div>
          <div className="mt-10 space-y-3">
            {[
              ['Il contenuto può essere recuperato dopo la scadenza?', 'No. Alla scadenza (o al raggiungimento del limite di visualizzazioni) il record viene eliminato dal database. Non esiste backup.'],
              ['Serve un account per usare il servizio?', 'No. Il servizio funziona senza registrazione. Una dashboard di sessione ti mostra i push creati dal tuo browser.'],
              ['Che tipo di file posso caricare?', 'Qualsiasi tipo, fino a 5MB per file. Configurazioni, PDF, immagini, chiavi.'],
              ['I dati sono cifrati?', 'Sì. Ogni contenuto è cifrato con AES-256 tramite Fernet prima di essere salvato.'],
              ['È conforme al GDPR?', 'Sì. Nessun profilo utente, nessuna profilazione, dati in EU, principio di minimizzazione.'],
              ['Posso distruggere un push in anticipo?', 'Sì. Dalla dashboard di sessione o dalla pagina di visualizzazione (se abilitato).'],
            ].map(([q, a]) => (
              <Card key={q} className="border border-slate-200 p-5">
                <div className="font-semibold text-slate-900">{q}</div>
                <div className="mt-1.5 text-sm text-slate-600">{a}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Zap className="h-10 w-10 mx-auto text-[#0d63b0]" />
          <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-slate-900">Pronto? Crea il tuo push in 10 secondi.</h2>
          <p className="mt-3 text-slate-600">Nessuna installazione, nessun account, nessun costo.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white h-12 px-6">
              <Link to="/p/new">Crea Push <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 px-6">
              <Link to="/dashboard">Vai alla dashboard</Link>
            </Button>
          </div>
          <div className="mt-6 text-xs text-slate-500 inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Verona, Italia · Tecnologie srl</div>
        </div>
      </section>
    </div>
  );
}
